﻿/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {
    ColumnDef,
    ColumnFiltersState,
    ColumnOrderState,
    getCoreRowModel,
    PaginationState,
    SortingState,
    useReactTable,
    VisibilityState,
} from '@tanstack/react-table'
import { useDebouncedCallback } from 'utils/hooks/useDebounce'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useAppDispatch } from 'helpers/store'
import { useTypedSelector } from 'helpers/reducers'
import { setTableVisibility, setTableFiltersVisible } from 'store/ui/UiReducer'
import { applyTableColumnWidths } from 'utils/table/tableColumnWidths'
import { buildSelectColumn, MASS_ACTIONS_SELECT_COLUMN_ID } from 'components/Ui/Tanstack/TableMassActions/selectColumn'
import { MassAction } from 'components/Ui/Tanstack/TableMassActions/types'
import {
    readPersistedTableColumnVisibility,
    writePersistedTableColumnVisibility,
} from 'utils/table/tableColumnVisibilityStorage'

type StateUpdater<T> = T | ((old: T) => T)

const EMPTY_VISIBILITY: VisibilityState = {}

const getPersistedVisibility = (tableId?: string) => {
    if (!tableId) {
        return null
    }
    return readPersistedTableColumnVisibility(tableId)
}

export interface ChangeCustomFilter {
    filterName: string
    data: unknown
}

export interface TableUiState {
    pageIndex: number
    pageSize: number
    globalFilter: string
    columnFilters: ColumnFiltersState
    sorting: SortingState
    columnVisibility?: VisibilityState
    columnOrder?: ColumnOrderState
}

export interface useTableProps<T> {
    defaultData?: T[]
    columns: ColumnDef<T, any>[]
    defaultColumnFilters?: ColumnFiltersState
    defaultGlobalFilter?: string
    enableRowSelection?: boolean
    defaultRecordsPerPage?: number
    defaultRecordsPage?: number
    defaultSorting?: SortingState
    defaultColumnVisibility?: VisibilityState
    defaultColumnOrder?: ColumnOrderState
    onUiStateChange?: (ui: TableUiState) => void
    tableId?: string
    showSkeletonLoading?: boolean
    disableRowSelection?: boolean
    massActions?: MassAction<T>[]
}

export type TableInit<T extends { id?: string | number }> = ReturnType<typeof useTable<T>>

/**
 * Untyped variant for shared utilities and sub-components that operate
 * across multiple entity types. Prefer `TableInit<YourEntity>` in
 * entity-specific components.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type TableInitBase = TableInit<any>

export const useTable = <T extends { id?: string | number }>(props: useTableProps<T>) => {
    const [recordsTable, setRecordsTable] = useState<T[]>(props?.defaultData ?? [])
    const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({})
    const [paginationTotal, setPaginationTotal] = useState<number>(0)
    const [recordsCount, setRecordsCount] = useState<number>(props?.defaultData ? props.defaultData.length : 0)
    const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
        pageIndex: props?.defaultRecordsPage ?? 0,
        pageSize: props?.defaultRecordsPerPage ?? 50,
    })
    const [columnFilters, setColumnFiltersState] = useState<ColumnFiltersState>(props.defaultColumnFilters ?? [])

    const dispatch = useAppDispatch()
    const persistedVisibility = React.useMemo(() => getPersistedVisibility(props.tableId), [props.tableId])
    const storedVisibility = useTypedSelector(
        (state) =>
            (props.tableId && state.ui.tables?.[props.tableId]) ||
            persistedVisibility ||
            props.defaultColumnVisibility ||
            EMPTY_VISIBILITY
    )

    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(storedVisibility)
    const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(props.defaultColumnOrder ?? [])

    const [globalFilter, setGlobalFilterState] = useState(props?.defaultGlobalFilter ?? '')
    const [sorting, setSorting] = useState<SortingState>(props.defaultSorting ?? [])
    const [isLoading, setIsLoading] = useState(false)
    const [isPageSizeChanging, setIsPageSizeChanging] = useState(false)
    const [isTableInteractionLoading, setIsTableInteractionLoading] = useState(false)
    const [lastColumnFilters, setLastColumnFilters] = useState<ColumnFiltersState>([])
    const storedColumnFiltersVisible = useTypedSelector((state) =>
        props.tableId ? state.ui.tableFiltersVisible?.[props.tableId] : undefined
    )
    const [columnFiltersVisible, setColumnFiltersVisibleState] = useState<boolean>(
        () => storedColumnFiltersVisible ?? false
    )
    useEffect(() => {
        if (!props.tableId || typeof storedColumnFiltersVisible !== 'boolean') {
            return
        }

        setColumnFiltersVisibleState(storedColumnFiltersVisible)
    }, [props.tableId, storedColumnFiltersVisible])

    const setColumnFiltersVisible = React.useCallback(
        (value: StateUpdater<boolean>) => {
            setColumnFiltersVisibleState((previousValue) => {
                const nextValue =
                    typeof value === 'function' ? (value as (old: boolean) => boolean)(previousValue) : value

                if (props.tableId) {
                    dispatch(
                        setTableFiltersVisible({
                            tableId: props.tableId,
                            visible: nextValue,
                        })
                    )
                }

                return nextValue
            })
        },
        [dispatch, props.tableId]
    )

    const toggleColumnFiltersVisible = React.useCallback(() => {
        setColumnFiltersVisible((previousValue) => !previousValue)
    }, [setColumnFiltersVisible])

    const prevUiStateRef = useRef<string>('')
    const pageSizeLoadingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const tableInteractionLoadingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const previousLoadingRef = useRef(false)
    const previousInteractionKeyRef = useRef<string>('')

    const isRowSelectionEnabled =
        !props.disableRowSelection &&
        (props.enableRowSelection ?? (props.massActions?.length ?? 0) > 0)

    const columnsWithSelection = useMemo(() => {
        if (!isRowSelectionEnabled) {
            return props.columns
        }
        if (props.columns.some((column) => column.id === MASS_ACTIONS_SELECT_COLUMN_ID)) {
            return props.columns
        }
        return [buildSelectColumn<T>(), ...props.columns]
    }, [props.columns, isRowSelectionEnabled])

    const effectiveColumnOrder = useMemo<ColumnOrderState>(() => {
        if (!isRowSelectionEnabled || columnOrder.length === 0) {
            return columnOrder
        }
        const withoutSelect = columnOrder.filter((id) => id !== MASS_ACTIONS_SELECT_COLUMN_ID)
        if (withoutSelect.length === columnOrder.length) {
            return [MASS_ACTIONS_SELECT_COLUMN_ID, ...columnOrder]
        }
        return [MASS_ACTIONS_SELECT_COLUMN_ID, ...withoutSelect]
    }, [columnOrder, isRowSelectionEnabled])

    const isSameArrayShallow = (a: T[], b: T[]) => {
        if (a.length !== b.length) {
            return false
        }

        for (let i = 0; i < a.length; i += 1) {
            if (!Object.is(a[i], b[i])) {
                return false
            }
        }

        return true
    }

    useEffect(() => {
        if (!props.defaultData) {
            return
        }

        setRecordsTable((prev) => {
            if (isSameArrayShallow(prev, props.defaultData as T[])) {
                return prev
            }

            return props.defaultData
        })
    }, [props.defaultData])

    useEffect(() => {
        const hasControlledPage = typeof props.defaultRecordsPage === 'number'
        const hasControlledPageSize = typeof props.defaultRecordsPerPage === 'number'

        if (!hasControlledPage && !hasControlledPageSize) {
            return
        }

        setPagination((prev) => {
            const nextPageIndex = hasControlledPage ? (props.defaultRecordsPage as number) : prev.pageIndex
            const nextPageSize = hasControlledPageSize ? (props.defaultRecordsPerPage as number) : prev.pageSize

            if (prev.pageIndex === nextPageIndex && prev.pageSize === nextPageSize) {
                return prev
            }

            return {
                pageIndex: nextPageIndex,
                pageSize: nextPageSize,
            }
        })
    }, [props.defaultRecordsPage, props.defaultRecordsPerPage])

    const clearNullableFilters = (items: ColumnFiltersState) => {
        const newItems = items.filter((item) => {
            if (Array.isArray(item.value)) {
                const empty = item.value.every((v) => v.length === 0)
                if (empty) return false
            }

            if (typeof item.value === 'object' && item.value !== null && !Array.isArray(item.value)) {
                const range = item.value as {
                    from?: unknown
                    to?: unknown
                    min?: unknown
                    max?: unknown
                }

                if ('from' in range || 'to' in range || 'min' in range || 'max' in range) {
                    const from = range.from ?? range.min
                    const to = range.to ?? range.max
                    const hasFrom = from !== undefined && from !== null && from !== ''
                    const hasTo = to !== undefined && to !== null && to !== ''
                    return hasFrom || hasTo
                }
            }

            return (
                (Array.isArray(item.value) && item.value.length > 0) ||
                typeof item.value === 'number' ||
                typeof item.value === 'string' ||
                (typeof item.value === 'boolean' && item.value) ||
                (typeof item.value === 'object' && item.value !== null)
            )
        })

        return newItems.length > 0 ? newItems : []
    }

    const getReservationIdsFromSelectedRows = () => {
        return Object.keys(rowSelection)
            .filter((key) => rowSelection[key])
            .map((key) => (isNaN(Number(key)) ? key : Number(key)))
    }

    const clearSelection = React.useCallback(() => {
        setRowSelection({})
    }, [])

    const getSelectedValueFilter = (filterId: string, defaultValue?: string | number | null | []) => {
        const findFilter = columnFilters.find((item) => item.id === filterId)
        return findFilter?.value ?? defaultValue ?? null
    }

    const resetPaginationToFirstPage = React.useCallback(() => {
        setPagination((prev) => (prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }))
    }, [])

    const setColumnFiltersWithPageReset = React.useCallback(
        (updater: StateUpdater<ColumnFiltersState>) => {
            resetPaginationToFirstPage()
            setColumnFiltersState(updater)
        },
        [resetPaginationToFirstPage]
    )

    const setGlobalFilterWithPageReset = React.useCallback(
        (updater: StateUpdater<string>) => {
            resetPaginationToFirstPage()
            setGlobalFilterState(updater)
        },
        [resetPaginationToFirstPage]
    )

    const setSortingWithPageReset = React.useCallback(
        (updater: StateUpdater<SortingState>) => {
            resetPaginationToFirstPage()
            setSorting(updater)
        },
        [resetPaginationToFirstPage]
    )

    useEffect(() => {
        if (typeof window === 'undefined') {
            return
        }

        const onTabChangeReset = () => {
            resetPaginationToFirstPage()
        }

        window.addEventListener('table:reset-pagination-first-page', onTabChangeReset)

        return () => {
            window.removeEventListener('table:reset-pagination-first-page', onTabChangeReset)
        }
    }, [resetPaginationToFirstPage])

    const onChangeSearchHandler = (data: string) => {
        setGlobalFilterWithPageReset(data)
    }

    const sortChangeHandler = useDebouncedCallback((headerId: string) => {
        const currentSort = sorting.find((item) => item.id === headerId)

        if (!currentSort) {
            setSortingWithPageReset([{ desc: true, id: headerId }])
        } else {
            if (currentSort.desc) {
                setSortingWithPageReset([{ desc: false, id: headerId }])
            } else {
                setSortingWithPageReset([])
            }
        }
    }, 300)

    const onChangeFilterHandler = (filterName: string, data: unknown) => {
        const itemIndex = columnFilters.findIndex((item) => item.id === filterName)
        const newFilter = structuredClone(columnFilters)
        if (itemIndex !== -1) {
            newFilter[itemIndex].value = data
            setColumnFiltersWithPageReset(newFilter)
        } else {
            setColumnFiltersWithPageReset([...newFilter, { id: filterName, value: data }])
        }
    }

    const changeFiltersHandler = (filtersData: ChangeCustomFilter[]) => {
        const tempColumnFilters = structuredClone(columnFilters)
        const newColumnFilters = filtersData.map((filter) => ({
            id: filter.filterName,
            value: filter.data,
        }))
        const mergedFilters = newColumnFilters.concat(
            tempColumnFilters.filter((a) => !newColumnFilters.find((b) => b.id === a.id))
        )
        setColumnFiltersWithPageReset(mergedFilters)
    }

    const removeColumnFilter = () => {}

    const pagination = React.useMemo(() => ({ pageIndex, pageSize }), [pageIndex, pageSize])

    const paginationSetPageIndexHandler = useDebouncedCallback((page: number) => table.setPageIndex(page), 300)
    const paginationNextPageHandler = useDebouncedCallback(() => table.nextPage(), 300)
    const paginationPreviousPageHandler = useDebouncedCallback(() => table.previousPage(), 300)
    const paginationSetPageSize = useDebouncedCallback((size: number) => {
        setIsPageSizeChanging(true)

        if (pageSizeLoadingTimeoutRef.current) {
            clearTimeout(pageSizeLoadingTimeoutRef.current)
        }

        pageSizeLoadingTimeoutRef.current = setTimeout(() => {
            setIsPageSizeChanging(false)
            pageSizeLoadingTimeoutRef.current = null
        }, 500)

        table.setPageSize(size)
    }, 300)

    const fallbackPageCount = React.useMemo(() => {
        const safePageSize = pageSize > 0 ? pageSize : 1
        const safeRecordsCount = Number.isFinite(recordsCount) ? recordsCount : 0
        const safeRowsCount = Array.isArray(recordsTable) ? recordsTable.length : 0
        const fallbackTotal = safeRecordsCount > 0 ? safeRecordsCount : safeRowsCount

        if (fallbackTotal <= 0) {
            return 0
        }

        return Math.max(1, Math.ceil(fallbackTotal / safePageSize))
    }, [pageSize, recordsCount, recordsTable])

    const effectivePageCount =
        Number.isFinite(paginationTotal) && paginationTotal > 0 ? paginationTotal : fallbackPageCount

    const sizedColumns = useMemo(
        () => applyTableColumnWidths(columnsWithSelection),
        [columnsWithSelection]
    )

    const table = useReactTable({
        data: recordsTable,
        columns: sizedColumns,
        meta: {
            tableId: props.tableId,
        },
        pageCount: effectivePageCount,
        getRowId: (row) => row.id?.toString() ?? '',
        state: {
            rowSelection,
            pagination,
            columnFilters,
            globalFilter,
            sorting,
            columnVisibility,
            columnOrder: effectiveColumnOrder,
        },
        enableRowSelection: isRowSelectionEnabled,
        onRowSelectionChange: setRowSelection,
        enableGlobalFilter: true,
        enableColumnFilters: true,
        manualFiltering: true,
        onColumnFiltersChange: setColumnFiltersWithPageReset,
        onGlobalFilterChange: setGlobalFilterWithPageReset,
        onPaginationChange: setPagination,
        onSortingChange: setSortingWithPageReset,
        onColumnVisibilityChange: setColumnVisibility,
        onColumnOrderChange: setColumnOrder,
        manualSorting: true,
        manualPagination: true,
        getCoreRowModel: getCoreRowModel(),
        debugTable: false,
    })

    useEffect(() => {
        const interactionKey = JSON.stringify({
            pageIndex,
            pageSize,
            globalFilter,
            columnFilters,
            sorting,
        })

        if (previousInteractionKeyRef.current === '') {
            previousInteractionKeyRef.current = interactionKey
            return
        }

        if (previousInteractionKeyRef.current === interactionKey) {
            return
        }

        previousInteractionKeyRef.current = interactionKey
        setIsTableInteractionLoading(true)

        if (tableInteractionLoadingTimeoutRef.current) {
            clearTimeout(tableInteractionLoadingTimeoutRef.current)
        }

        tableInteractionLoadingTimeoutRef.current = setTimeout(() => {
            setIsTableInteractionLoading(false)
            tableInteractionLoadingTimeoutRef.current = null
        }, 500)
    }, [pageIndex, pageSize, globalFilter, columnFilters, sorting])

    useEffect(() => {
        if (previousLoadingRef.current && !isLoading) {
            setIsTableInteractionLoading(false)

            if (tableInteractionLoadingTimeoutRef.current) {
                clearTimeout(tableInteractionLoadingTimeoutRef.current)
                tableInteractionLoadingTimeoutRef.current = null
            }

            if (isPageSizeChanging) {
                setIsPageSizeChanging(false)

                if (pageSizeLoadingTimeoutRef.current) {
                    clearTimeout(pageSizeLoadingTimeoutRef.current)
                    pageSizeLoadingTimeoutRef.current = null
                }
            }
        }

        previousLoadingRef.current = isLoading
    }, [isLoading, isPageSizeChanging, pageSize])

    useEffect(() => {
        if (typeof props.onUiStateChange === 'function') {
            const currentUiState = JSON.stringify({
                pageIndex,
                pageSize,
                globalFilter,
                columnFilters,
                sorting,
                columnVisibility,
                columnOrder,
            })

            if (prevUiStateRef.current !== currentUiState) {
                prevUiStateRef.current = currentUiState
                props.onUiStateChange({
                    pageIndex,
                    pageSize,
                    globalFilter,
                    columnFilters,
                    sorting,
                    columnVisibility,
                    columnOrder,
                })
            }
        }
    }, [pageIndex, pageSize, globalFilter, columnFilters, sorting, columnVisibility, columnOrder])

    useEffect(() => {
        if (props.tableId) {
            dispatch(
                setTableVisibility({
                    tableId: props.tableId,
                    visibility: columnVisibility,
                })
            )
            try {
                writePersistedTableColumnVisibility(props.tableId, columnVisibility)
            } catch (error) {
                console.warn('Failed to persist table visibility', error)
            }
        }
    }, [columnVisibility, dispatch, props.tableId])

    useEffect(() => {
        return () => {
            if (pageSizeLoadingTimeoutRef.current) {
                clearTimeout(pageSizeLoadingTimeoutRef.current)
            }
            if (tableInteractionLoadingTimeoutRef.current) {
                clearTimeout(tableInteractionLoadingTimeoutRef.current)
            }
        }
    }, [])

    const didMountRef = useRef(false)
    useEffect(() => {
        if (!didMountRef.current) {
            didMountRef.current = true
            return
        }
        if (Object.keys(rowSelection).length > 0) {
            setRowSelection({})
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columnFilters, globalFilter, sorting])

    const selectedRowIds = React.useMemo<(string | number)[]>(() => {
        return Object.keys(rowSelection)
            .filter((key) => rowSelection[key])
            .map((key) => (isNaN(Number(key)) ? key : Number(key)))
    }, [rowSelection])

    const selectedRows = React.useMemo<T[]>(() => {
        return table.getSelectedRowModel().rows.map((row) => row.original as T)
    }, [table, rowSelection])

    const selectedCount = selectedRowIds.length
    const hasSelection = selectedCount > 0
    const computedShowSkeletonLoading = isLoading || isPageSizeChanging || isTableInteractionLoading

    return React.useMemo(
        () => ({
            table,
            tableId: props.tableId,
            isLoading,
            isPageSizeChanging,
            isTableInteractionLoading,
            setIsLoading,
            recordsCount,
            setRecordsCount,
            lastColumnFilters,
            setLastColumnFilters,
            sortChangeHandler,
            clearNullableFilters,
            onChangeSearchHandler,
            getSelectedValueFilter,
            paginationSetPageIndexHandler,
            paginationNextPageHandler,
            paginationPreviousPageHandler,
            paginationSetPageSize,
            recordsTable,
            setRecordsTable,
            setPagination,
            paginationTotal,
            setPaginationTotal,
            columnFilters,
            setColumnFilters: setColumnFiltersWithPageReset,
            globalFilter,
            setGlobalFilter: setGlobalFilterWithPageReset,
            pageIndex,
            pageSize,
            sorting,
            setRowSelection,
            rowSelection,
            getReservationIdsFromSelectedRows,
            clearSelection,
            selectedRowIds,
            selectedRows,
            selectedCount,
            hasSelection,
            isRowSelectionEnabled,
            onChangeFilterHandler,
            removeColumnFilter,
            changeFiltersHandler,
            setSorting: setSortingWithPageReset,
            columnVisibility,
            setColumnVisibility,
            columnOrder,
            setColumnOrder,
            columnFiltersVisible,
            setColumnFiltersVisible,
            toggleColumnFiltersVisible,
            showSkeletonLoading: props.showSkeletonLoading ?? computedShowSkeletonLoading,
            massActions: props.massActions ?? [],
        }),
        [
            table,
            props.tableId,
            isLoading,
            isPageSizeChanging,
            isTableInteractionLoading,
            recordsCount,
            lastColumnFilters,
            sortChangeHandler,
            paginationSetPageIndexHandler,
            paginationNextPageHandler,
            paginationPreviousPageHandler,
            paginationSetPageSize,
            recordsTable,
            paginationTotal,
            columnFilters,
            globalFilter,
            pageIndex,
            pageSize,
            sorting,
            rowSelection,
            columnVisibility,
            columnOrder,
            columnFiltersVisible,
            toggleColumnFiltersVisible,
            selectedRowIds,
            selectedRows,
            selectedCount,
            hasSelection,
            isRowSelectionEnabled,
            clearSelection,
            props.showSkeletonLoading,
            props.massActions,
            computedShowSkeletonLoading,
            setColumnFiltersWithPageReset,
            setGlobalFilterWithPageReset,
            setSortingWithPageReset,
        ]
    )
}
